/**
 * SplitCartModal - Sepet Bölme Modalı
 * Sol ve sağ sepetleri yan yana gösterir, ürün taşıma ve ayrı ödeme imkanı sağlar
 * 
 * MODLAR:
 * - Normal Hızlı Satış: Local state kullanır, ödeme alınana kadar SQL'e kaydetmez
 * - Restoran Modu: SQL'e kayıt eder (RestaurantActiveSales tablosu - IsSplitParent, SplitParentId, SplitIndex)
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Chip,
  TextField,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MergeIcon from '@mui/icons-material/MergeType';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import type { CartItem } from '../../types/product.types';
import type { SplitCartModalProps } from '../../types/splitCart.types';
import { calculateCartTotals } from '../../hooks/useSplitCart';
import { restaurantSplitApi, RestaurantActiveSaleDto } from '../../../../../services/restaurantApiService';

// ============================================
// CART ITEM COMPONENT
// ============================================

interface CartItemRowProps {
  item: CartItem;
  onMoveClick: (itemId: string, quantity?: number) => void;
  moveDirection: 'left' | 'right';
  isSelected?: boolean;
  onQuantityChange?: (itemId: string, quantity: number) => void;
}

const CartItemRow: React.FC<CartItemRowProps> = ({
  item,
  onMoveClick,
  moveDirection,
  isSelected,
  onQuantityChange,
}) => {
  const [moveQuantity, setMoveQuantity] = useState<number>(1);

  const handleMoveAll = () => {
    onMoveClick(item.id);
  };

  const handleMovePartial = () => {
    if (moveQuantity > 0 && moveQuantity <= item.quantity) {
      onMoveClick(item.id, moveQuantity);
    }
  };

  return (
    <ListItem
      sx={{
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        borderRadius: 1,
        mb: 0.5,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight="medium">
              {item.name}
            </Typography>
            <Chip
              label={`x${item.quantity}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        }
        secondary={
          <Typography variant="caption" color="text.secondary">
            {item.price.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} / adet
          </Typography>
        }
      />
      <ListItemSecondaryAction>
        <Box display="flex" alignItems="center" gap={0.5}>
          {item.quantity > 1 && (
            <>
              <TextField
                type="number"
                size="small"
                value={moveQuantity}
                onChange={(e) => setMoveQuantity(Math.max(1, Math.min(item.quantity, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: item.quantity, style: { width: 40, textAlign: 'center' } }}
                sx={{ width: 60 }}
              />
              <Tooltip title={`${moveQuantity} adet taşı`}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleMovePartial}
                >
                  {moveDirection === 'right' ? <ArrowForwardIcon fontSize="small" /> : <ArrowBackIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Tümünü taşı">
            <IconButton
              size="small"
              color="secondary"
              onClick={handleMoveAll}
            >
              {moveDirection === 'right' ? (
                <ArrowForwardIcon />
              ) : (
                <ArrowBackIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

// ============================================
// CART PANEL COMPONENT
// ============================================

interface CartPanelProps {
  title: string;
  invoiceNumber: string;
  items: CartItem[];
  total: number;
  onMoveItem: (itemId: string, quantity?: number) => void;
  moveDirection: 'left' | 'right';
  onCashPayment: () => void;
  onPosPayment: () => void;
  onPartialPayment: () => void;
  isPaymentDisabled?: boolean;
  color: 'primary' | 'secondary';
  isRestaurantMode?: boolean; // ✅ YENİ: Restoran modunda fiyatlar KDV dahil
}

const CartPanel: React.FC<CartPanelProps> = ({
  title,
  invoiceNumber,
  items,
  total,
  onMoveItem,
  moveDirection,
  onCashPayment,
  onPosPayment,
  onPartialPayment,
  isPaymentDisabled,
  color,
  isRestaurantMode = false,
}) => {
  // ✅ Restoran modunda fiyatlar zaten KDV dahil, ayrıca KDV ekleme
  const totals = useMemo(() => calculateCartTotals(items, isRestaurantMode), [items, isRestaurantMode]);

  return (
    <Paper
      elevation={3}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderTop: 3,
        borderColor: `${color}.main`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ShoppingCartIcon color={color} />
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
          </Box>
          <Chip
            label={invoiceNumber}
            size="small"
            variant="outlined"
            color={color}
          />
        </Box>
      </Box>

      {/* Items List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1, minHeight: 200 }}>
        {items.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            color="text.secondary"
          >
            <Typography variant="body2">Sepet boş</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {items.map((item) => (
              <CartItemRow
                key={item.id}
                item={item}
                onMoveClick={onMoveItem}
                moveDirection={moveDirection}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Totals */}
      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2" color="text.secondary">
            Ara Toplam:
          </Typography>
          <Typography variant="body2">
            {totals.subtotal.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
          </Typography>
        </Box>
        {totals.discountTotal > 0 && (
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              İndirim:
            </Typography>
            <Typography variant="body2" color="error.main">
              -{totals.discountTotal.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 0.5 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight="bold">
            TOPLAM:
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" color={`${color}.main`}>
            {totals.grandTotal.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
          </Typography>
        </Box>
      </Box>

      {/* Payment Buttons */}
      <Box sx={{ p: 1, display: 'flex', gap: 1, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<AttachMoneyIcon />}
          onClick={onCashPayment}
          disabled={isPaymentDisabled || items.length === 0}
          sx={{ flex: 1 }}
        >
          NAKİT
        </Button>
        <Button
          variant="contained"
          color="info"
          size="small"
          startIcon={<CreditCardIcon />}
          onClick={onPosPayment}
          disabled={isPaymentDisabled || items.length === 0}
          sx={{ flex: 1 }}
        >
          POS
        </Button>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<ReceiptIcon />}
          onClick={onPartialPayment}
          disabled={isPaymentDisabled || items.length === 0}
          sx={{ flex: 1 }}
        >
          PARÇALI
        </Button>
      </Box>
    </Paper>
  );
};

// ============================================
// MAIN MODAL COMPONENT
// ============================================

const SplitCartModal: React.FC<SplitCartModalProps> = ({
  open,
  onClose,
  isRestaurantMode,
  tableId,
  tableName,
  restaurantScreenId,
  companyId,
  mainCart,
  mainInvoiceNumber,
  setCart,
  showSnackbar,
  onSplitComplete,
  waiterName,
  screenSettings,
  splitRightCart,
  onRightCartChange,
  splitRightInvoiceNumber,
  onRightInvoiceNumberChange,
  splitBIndex = 2,
  onSplitBIndexChange,
  refreshKey,
  onProcessPayment,
  onRestaurantPaymentComplete, // ✅ YENİ: Restoran modu için tüm ödemeler tamamlandığında çağrılır
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Local state for split carts
  const [leftCart, setLeftCart] = useState<CartItem[]>([]);
  const [rightCart, setRightCart] = useState<CartItem[]>([]);
  const [rightInvoiceNumber, setRightInvoiceNumber] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const isInitializedRef = useRef(false); // İlk açılışı kontrol et (ref ile sync)
  const prevOpenRef = useRef(false); // Önceki open değeri
  
  // ✅ FIX: Closure sorununu önlemek için güncel değerleri ref'te tut
  const leftCartRef = useRef<CartItem[]>([]);
  const rightCartRef = useRef<CartItem[]>([]);
  const rightInvoiceNumberRef = useRef<string>('');
  
  // ✅ YENİ: Ödeme yapıldığında modal kapanırken setCart yapılmaması için flag
  const paymentJustCompletedRef = useRef(false);
  
  // ✅ RESTORAN MODU: SQL state'leri
  const [mainSaleId, setMainSaleId] = useState<number | null>(null);
  const [splitSaleId, setSplitSaleId] = useState<number | null>(null);
  const [sqlLeftItems, setSqlLeftItems] = useState<any[]>([]); // SQL'deki sol sepet ürünleri
  const [sqlRightItems, setSqlRightItems] = useState<any[]>([]); // SQL'deki sağ sepet ürünleri
  
  // State'ler değiştiğinde ref'leri güncelle
  useEffect(() => {
    leftCartRef.current = leftCart;
  }, [leftCart]);
  
  useEffect(() => {
    rightCartRef.current = rightCart;
  }, [rightCart]);
  
  useEffect(() => {
    rightInvoiceNumberRef.current = rightInvoiceNumber;
  }, [rightInvoiceNumber]);

  // ✅ YENİ: refreshKey değiştiğinde state'leri prop'lardan yeniden yükle
  // Parçalı ödeme tamamlandığında kullanılır
  const prevRefreshKeyRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    // refreshKey tanımlıysa ve değiştiyse
    if (refreshKey !== undefined && refreshKey !== prevRefreshKeyRef.current && prevRefreshKeyRef.current !== undefined) {
      // Sol sepeti ana sepetten yükle
      setLeftCart([...mainCart]);
      leftCartRef.current = [...mainCart];
      
      // Sağ sepeti dışarıdan gelen değerden yükle
      if (splitRightCart && splitRightCart.length > 0) {
        setRightCart([...splitRightCart]);
        rightCartRef.current = [...splitRightCart];
      } else {
        setRightCart([]);
        rightCartRef.current = [];
      }
      
      // Sağ sepet fiş numarasını güncelle
      if (splitRightInvoiceNumber) {
        setRightInvoiceNumber(splitRightInvoiceNumber);
        rightInvoiceNumberRef.current = splitRightInvoiceNumber;
      } else {
        setRightInvoiceNumber('');
        rightInvoiceNumberRef.current = '';
      }
    }
    prevRefreshKeyRef.current = refreshKey;
  }, [refreshKey, mainCart, splitRightCart, splitRightInvoiceNumber]);

  /**
   * ✅ FIX: SQL item → CartItem dönüşümü için tek bir helper.
   * Move-item sonrası cart state'lerini backend response'undan türetmek için
   * kullanıyoruz — böylece prev-based aritmetik ile backend arasındaki
   * adet desync'leri ortadan kalkıyor.
   */
  const mapSqlItemToCartItem = useCallback((item: any): CartItem => ({
    id: item.ProductId,
    cartItemId: `sql-${item.Id}`,
    name: item.ProductName,
    price: item.UnitPrice,
    quantity: item.Quantity,
    vatRate: item.VatRate || 0,
    vatStatus: 'included' as const,
    sqlItemId: item.Id,
  } as CartItem), []);

  /**
   * ✅ RESTORAN MODU: SQL'den sepetleri yükle
   */
  const loadSplitCartsFromSQL = useCallback(async () => {
    if (!isRestaurantMode || !tableId) return;
    
    try {
      setIsLoading(true);
      
      // ✅ MULTI-TENANT: companyId parametresi ile güvenli sorgu
      const response = await restaurantSplitApi.getByTableId(tableId);
      
      if (response.Success && response.Data) {
        const sales = response.Data as RestaurantActiveSaleDto[];
        const mainSale = sales.find(s => s.SplitParentId === null || s.SplitParentId === undefined);
        const splitSale = sales.find(s => s.SplitParentId !== null && s.SplitParentId !== undefined);
        
        if (mainSale) {
          setMainSaleId(mainSale.Id);
          setSqlLeftItems(mainSale.Items || []);
          
          // Sol sepeti SQL'den yükle
          const leftItems: CartItem[] = (mainSale.Items || []).map(mapSqlItemToCartItem);
          setLeftCart(leftItems);
          leftCartRef.current = leftItems;
          
          // Sol sepet fiş numarasını set et
          if (mainSale.ReservedLongCode) {
            // mainInvoiceNumber zaten prop olarak geliyor
          }
        }
        
        if (splitSale) {
          setSplitSaleId(splitSale.Id);
          setSqlRightItems(splitSale.Items || []);
          
          // Sağ sepeti SQL'den yükle
          const rightItems: CartItem[] = (splitSale.Items || []).map(mapSqlItemToCartItem);
          setRightCart(rightItems);
          rightCartRef.current = rightItems;
          
          // Sağ sepet fiş numarasını set et
          if (splitSale.ReservedLongCode) {
            setRightInvoiceNumber(splitSale.ReservedLongCode);
            rightInvoiceNumberRef.current = splitSale.ReservedLongCode;
            onRightInvoiceNumberChange?.(splitSale.ReservedLongCode);
          }
        }
        
      }
    } catch (error) {
      console.error('❌ [SEPET BÖL] SQL yükleme hatası:', error);
      showSnackbar('Sepetler yüklenirken hata oluştu!', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [isRestaurantMode, tableId, companyId, showSnackbar, onRightInvoiceNumberChange, mapSqlItemToCartItem]);

  // Initialize carts ONLY when modal first opens
  useEffect(() => {
    // Modal yeni açıldıysa (önceden kapalıydı, şimdi açık)
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    
    if (justOpened) {
      isInitializedRef.current = true;
      
      // ✅ YENİ: Ödeme flag'ini sıfırla
      paymentJustCompletedRef.current = false;
      
      // ✅ RESTORAN MODU: SQL'den yükle
      if (isRestaurantMode && tableId) {
        loadSplitCartsFromSQL();
        return; // SQL yüklemesi async olduğu için burada çık
      }
      
      // NORMAL HIZLI SATIŞ MODU: Local state kullan
      setLeftCart([...mainCart]);
      
      // Sağ sepet: dışarıdan gelen varsa onu kullan, yoksa boş başla
      if (splitRightCart && splitRightCart.length > 0) {
        setRightCart([...splitRightCart]);
      } else {
        setRightCart([]);
      }
      
      // Sağ sepet fiş numarası: dışarıdan gelen varsa onu kullan
      if (splitRightInvoiceNumber) {
        setRightInvoiceNumber(splitRightInvoiceNumber);
      } else {
        // Yoksa mainInvoiceNumber'dan türet (B index kullan)
        if (mainInvoiceNumber) {
          const newSplitInvoiceNumber = `${mainInvoiceNumber}-B${splitBIndex}`;
          setRightInvoiceNumber(newSplitInvoiceNumber);
          onRightInvoiceNumberChange?.(newSplitInvoiceNumber);
        } else {
          setRightInvoiceNumber('');
        }
      }
    }
    
    // Modal kapandığında initialized flag'ini sıfırla
    if (!open) {
      isInitializedRef.current = false;
    }
  }, [open, mainCart, mainInvoiceNumber, splitRightCart, splitRightInvoiceNumber, splitBIndex, onRightInvoiceNumberChange, isRestaurantMode, tableId, loadSplitCartsFromSQL]);
  
  // Sağ sepet değiştiğinde dışarıya bildir (sadece initialized ise)
  useEffect(() => {
    if (open && isInitializedRef.current && onRightCartChange) {
      onRightCartChange(rightCart);
    }
  }, [open, rightCart, onRightCartChange]);

  // Calculate totals
  // ✅ Restoran modunda fiyatlar zaten KDV dahil, ayrıca KDV ekleme
  const leftTotals = useMemo(() => calculateCartTotals(leftCart, isRestaurantMode), [leftCart, isRestaurantMode]);
  const rightTotals = useMemo(() => calculateCartTotals(rightCart, isRestaurantMode), [rightCart, isRestaurantMode]);

  /**
   * Modal kapandığında sol sepeti ana sepete kaydet
   */
  const handleCloseAndSave = async () => {
    // Ödeme yapıldıysa setCart yapma (çünkü successCallback içinde zaten yapıldı)
    if (paymentJustCompletedRef.current) {
      paymentJustCompletedRef.current = false;
      onClose();
      return;
    }
    
    // ✅ FIX: Closure sorunu - ref'ten güncel değerleri al
    const currentLeftCart = leftCartRef.current;
    const currentRightCart = rightCartRef.current;
    
    // RESTORAN MODU: Eğer sağ sepet boşsa split child satırını sil
    if (isRestaurantMode && tableId && currentRightCart.length === 0) {
      try {
        await restaurantSplitApi.mergeAll(tableId);
        setSplitSaleId(null);
        setSqlRightItems([]);
      } catch (error) {
        console.error('❌ [SEPET BÖL] Split temizleme hatası:', error);
        // ✅ FIX: Sessizce kalma, stale splitSaleId'yi temizle ki bir sonraki açılışta
        // loadSplitCartsFromSQL fresh durumu yüklesin.
        setSplitSaleId(null);
        setSqlRightItems([]);
      }
    }
    
    // Sol sepeti ana sepete kaydet
    setCart(currentLeftCart);
    
    // Sağ sepet değişikliklerini dışarıya bildir
    if (currentRightCart.length > 0) {
      onRightCartChange?.(currentRightCart);
    }
    
    onClose();
  };

  /**
   * Ürünü sağ sepete taşı
   */
  const handleMoveToRight = async (itemId: string, quantity?: number) => {
    const sourceItem = leftCart.find((item) => item.id === itemId);
    if (!sourceItem) return;

    const moveQty = quantity ?? sourceItem.quantity;

    // ✅ FIX: Sağ sepet boşsa ve fiş numarası yoksa, yeni fiş numarası oluştur
    // (Ödeme sonrası tekrar ürün taşındığında yeni numara almak için)
    // ⚠️ IMPORTANT: State closure sorunu nedeniyle rightCartRef kullanılıyor!
    const currentRightCart = rightCartRef.current;
    const currentRightInvoiceNumber = rightInvoiceNumberRef.current;
    
    console.log(`🔍 [SPLIT CART] handleMoveToRight kontrol:`, {
      rightCartRefLength: currentRightCart.length,
      rightCartStateLength: rightCart.length,
      currentRightInvoiceNumber,
      mainInvoiceNumber,
      splitBIndex,
      isRestaurantMode
    });
    
    // ✅ RESTORAN MODU: SQL'de split sepet oluştur/kullan
    if (isRestaurantMode && tableId && mainSaleId) {
      try {
        let targetSplitSaleId = splitSaleId;
        
        // Eğer henüz split sepet yoksa, SQL'de oluştur
        if (!targetSplitSaleId) {
          console.log('🔀 [SPLIT CART - SQL] Yeni split sepet oluşturuluyor...', { companyId });
          
          // ✅ MULTI-TENANT: companyId ile güvenli sepet bölme
          const splitResponse = await restaurantSplitApi.splitTable({
            TableId: tableId,
            OpenedBy: waiterName || undefined,
            ReservedLongCode: `${mainInvoiceNumber}-B${splitBIndex}`,
            ReservedShortCode: undefined
          });
          
          if (splitResponse.Success && splitResponse.SplitSaleId) {
            targetSplitSaleId = splitResponse.SplitSaleId;
            setSplitSaleId(targetSplitSaleId);
            
            // Fiş numarasını güncelle
            const newSplitInvoiceNumber = `${mainInvoiceNumber}-B${splitBIndex}`;
            setRightInvoiceNumber(newSplitInvoiceNumber);
            rightInvoiceNumberRef.current = newSplitInvoiceNumber;
            onRightInvoiceNumberChange?.(newSplitInvoiceNumber);
            
            console.log('✅ [SPLIT CART - SQL] Split sepet oluşturuldu:', targetSplitSaleId);
          } else {
            showSnackbar('Split sepet oluşturulamadı!', 'error');
            return;
          }
        }
        
        // SQL'de ürünü taşı
        const sqlItem = sqlLeftItems.find(i => i.ProductId === itemId);
        if (sqlItem) {
          console.log('📦 [SPLIT CART - SQL] Ürün taşınıyor:', sqlItem.Id, '→', targetSplitSaleId, { companyId });
          
          // ✅ MULTI-TENANT: companyId ile güvenli ürün taşıma
          const moveResponse = await restaurantSplitApi.moveItem({
            ItemId: sqlItem.Id,
            FromSaleId: mainSaleId,
            ToSaleId: targetSplitSaleId,
            Quantity: moveQty,
            MovedBy: waiterName || undefined,
          });
          
          if (!moveResponse.Success) {
            showSnackbar('Ürün taşınamadı!', 'error');
            return;
          }
          
          console.log('✅ [SPLIT CART - SQL] Ürün taşındı');
          
          // Backend'den güncel item listelerini al (ID senkronizasyonu için)
          if (moveResponse.FromItems && moveResponse.ToItems) {
            setSqlLeftItems(moveResponse.FromItems);
            setSqlRightItems(moveResponse.ToItems);

            // ✅ FIX (adet artışı bug'ı): UI cart'larını da backend response'undan türet.
            // Önceden prev-based aritmetik (`setLeftCart(prev => ...)`) kullanılıyordu;
            // SQL state ile lokal UI state desync olunca ardışık taşımalarda adetler
            // yanlış hesaplanabiliyordu. Tek güvenli kaynak: backend response.
            const newLeftCart = (moveResponse.FromItems as any[]).map(mapSqlItemToCartItem);
            const newRightCart = (moveResponse.ToItems as any[]).map(mapSqlItemToCartItem);
            setLeftCart(newLeftCart);
            leftCartRef.current = newLeftCart;
            setRightCart(newRightCart);
            rightCartRef.current = newRightCart;
            return; // Lokal fallback aritmetiğini atla
          } else {
            // Fallback: lokal state güncelle
            if (moveQty >= sqlItem.Quantity) {
              setSqlLeftItems(prev => prev.filter(i => i.Id !== sqlItem.Id));
            } else {
              setSqlLeftItems(prev => prev.map(i => 
                i.Id === sqlItem.Id ? { ...i, Quantity: i.Quantity - moveQty } : i
              ));
            }
            setSqlRightItems(prev => {
              const existingIdx = prev.findIndex(i => i.ProductId === itemId);
              if (existingIdx >= 0) {
                const updated = [...prev];
                updated[existingIdx] = { ...updated[existingIdx], Quantity: updated[existingIdx].Quantity + moveQty };
                return updated;
              } else {
                return [...prev, { ...sqlItem, Quantity: moveQty, ActiveSaleId: targetSplitSaleId }];
              }
            });
          }
        }
      } catch (error: any) {
        // ✅ FIX: Stale split state / 404 / 409 durumunda otomatik toparlanma
        const status = error?.response?.status;
        const code = error?.response?.data?.Code;
        const isStale = status === 404 || status === 409 || code === 'SPLIT_STATE_STALE';

        console.error('❌ [SPLIT CART - SQL] Ürün taşıma hatası:', error);

        if (isStale) {
          console.warn('🔁 [SPLIT CART - SQL] Stale state algılandı, sepet backend\'den yeniden yükleniyor...');
          setSplitSaleId(null);
          try {
            await loadSplitCartsFromSQL();
            showSnackbar('Sepet bilgisi güncellendi, lütfen tekrar deneyin.', 'warning');
          } catch {
            showSnackbar('Sepet yenilenirken hata oluştu!', 'error');
          }
        } else {
          showSnackbar('Ürün taşınırken hata oluştu!', 'error');
        }
        return;
      }
    } else {
      // NORMAL HIZLI SATIŞ: Sadece local state güncelle
      if (currentRightCart.length === 0 && !currentRightInvoiceNumber && mainInvoiceNumber) {
        const newSplitInvoiceNumber = `${mainInvoiceNumber}-B${splitBIndex}`;
        console.log(`🔢 [SPLIT CART] Sağ sepete ilk ürün ekleniyor, yeni fiş numarası: ${newSplitInvoiceNumber}`);
        setRightInvoiceNumber(newSplitInvoiceNumber);
        onRightInvoiceNumberChange?.(newSplitInvoiceNumber);
      }
    }

    // Update left cart + ref'i de güncelle (closure sorunu için)
    if (moveQty >= sourceItem.quantity) {
      setLeftCart((prev) => {
        const newCart = prev.filter((item) => item.id !== itemId);
        leftCartRef.current = newCart; // ✅ FIX: Ref'i de hemen güncelle
        return newCart;
      });
    } else {
      setLeftCart((prev) => {
        const newCart = prev.map((item) =>
          item.id === itemId ? { ...item, quantity: item.quantity - moveQty } : item
        );
        leftCartRef.current = newCart; // ✅ FIX: Ref'i de hemen güncelle
        return newCart;
      });
    }

    // Update right cart + ref'i de güncelle (closure sorunu için)
    setRightCart((prev) => {
      const existing = prev.find((item) => item.id === itemId);
      let newCart: CartItem[];
      if (existing) {
        newCart = prev.map((item) =>
          item.id === itemId ? { ...item, quantity: item.quantity + moveQty } : item
        );
      } else {
        newCart = [...prev, { ...sourceItem, quantity: moveQty }];
      }
      // ✅ FIX: Ref'i de hemen güncelle (next render'ı beklemeden)
      rightCartRef.current = newCart;
      return newCart;
    });
  };

  /**
   * Ürünü sol sepete taşı
   */
  const handleMoveToLeft = async (itemId: string, quantity?: number) => {
    const sourceItem = rightCart.find((item) => item.id === itemId);
    if (!sourceItem) return;

    const moveQty = quantity ?? sourceItem.quantity;

    // ✅ RESTORAN MODU: SQL'de ürünü geri taşı
    if (isRestaurantMode && tableId && mainSaleId && splitSaleId) {
      try {
        const sqlItem = sqlRightItems.find(i => i.ProductId === itemId);
        if (sqlItem) {
          console.log('📦 [SPLIT CART - SQL] Ürün geri taşınıyor:', sqlItem.Id, '→', mainSaleId, { companyId });
          
          // ✅ MULTI-TENANT: companyId ile güvenli ürün taşıma
          const moveResponse = await restaurantSplitApi.moveItem({
            ItemId: sqlItem.Id,
            FromSaleId: splitSaleId,
            ToSaleId: mainSaleId,
            Quantity: moveQty,
            MovedBy: waiterName || undefined,
          });
          
          if (!moveResponse.Success) {
            showSnackbar('Ürün taşınamadı!', 'error');
            return;
          }
          
          console.log('✅ [SPLIT CART - SQL] Ürün geri taşındı');
          
          // Backend'den güncel item listelerini al (ID senkronizasyonu için)
          if (moveResponse.FromItems && moveResponse.ToItems) {
            setSqlRightItems(moveResponse.FromItems);
            setSqlLeftItems(moveResponse.ToItems);

            // ✅ FIX (adet artışı bug'ı): UI cart'larını backend response'undan türet.
            // Prev-based aritmetik ile backend arasındaki desync'i böylece kırıyoruz.
            const newRightCart = (moveResponse.FromItems as any[]).map(mapSqlItemToCartItem);
            const newLeftCart = (moveResponse.ToItems as any[]).map(mapSqlItemToCartItem);
            setRightCart(newRightCart);
            rightCartRef.current = newRightCart;
            setLeftCart(newLeftCart);
            leftCartRef.current = newLeftCart;
            
            // Sağ sepet boşaldıysa split satırını sil
            if (moveResponse.FromItems.length === 0) {
              console.log('🗑️ [SPLIT CART - SQL] Sağ sepette ürün kalmadı, split child satırı siliniyor...', { splitSaleId, tableId });
              try {
                const mergeResponse = await restaurantSplitApi.mergeAll(tableId);
                console.log('✅ [SPLIT CART - SQL] Split child satırı silindi:', mergeResponse);
                setSplitSaleId(null);
              } catch (mergeError) {
                console.error('❌ [SPLIT CART - SQL] Split child satırı silinirken hata:', mergeError);
                // ✅ FIX: mergeAll patlasa bile stale state ile devam etmeyelim.
                // splitSaleId'yi null'lıyoruz ki bir sonraki "sağa taşı" yeni split oluştursun.
                setSplitSaleId(null);
                try {
                  await loadSplitCartsFromSQL();
                } catch { /* sessiz */ }
              }
            }
            return; // ✅ FIX: Lokal prev-based aritmetiği atla (backend response zaten tam state verdi)
          } else {
            // Fallback: lokal state güncelle
            if (moveQty >= sqlItem.Quantity) {
              setSqlRightItems(prev => prev.filter(i => i.Id !== sqlItem.Id));
              const remainingItems = sqlRightItems.filter(i => i.Id !== sqlItem.Id);
              if (remainingItems.length === 0) {
                console.log('🗑️ [SPLIT CART - SQL] Sağ sepette ürün kalmadı, split child satırı hemen siliniyor...', { splitSaleId, tableId });
                try {
                  const mergeResponse = await restaurantSplitApi.mergeAll(tableId);
                  console.log('✅ [SPLIT CART - SQL] Split child satırı silindi:', mergeResponse);
                  setSplitSaleId(null);
                } catch (mergeError) {
                  console.error('❌ [SPLIT CART - SQL] Split child satırı silinirken hata:', mergeError);
                  // ✅ FIX: mergeAll hatasında stale kalmasın
                  setSplitSaleId(null);
                  try {
                    await loadSplitCartsFromSQL();
                  } catch { /* sessiz */ }
                }
              }
            } else {
              setSqlRightItems(prev => prev.map(i => 
                i.Id === sqlItem.Id ? { ...i, Quantity: i.Quantity - moveQty } : i
              ));
            }
            setSqlLeftItems(prev => {
              const existingIdx = prev.findIndex(i => i.ProductId === itemId);
              if (existingIdx >= 0) {
                const updated = [...prev];
                updated[existingIdx] = { ...updated[existingIdx], Quantity: updated[existingIdx].Quantity + moveQty };
                return updated;
              } else {
                return [...prev, { ...sqlItem, Quantity: moveQty, ActiveSaleId: mainSaleId }];
              }
            });
          }
        }
      } catch (error: any) {
        // ✅ FIX: Stale split state / 404 / 409 durumunda otomatik toparlanma
        const status = error?.response?.status;
        const code = error?.response?.data?.Code;
        const isStale = status === 404 || status === 409 || code === 'SPLIT_STATE_STALE';

        console.error('❌ [SPLIT CART - SQL] Ürün geri taşıma hatası:', error);

        if (isStale) {
          console.warn('🔁 [SPLIT CART - SQL] Stale state algılandı, sepet backend\'den yeniden yükleniyor...');
          setSplitSaleId(null);
          try {
            await loadSplitCartsFromSQL();
            showSnackbar('Sepet bilgisi güncellendi, lütfen tekrar deneyin.', 'warning');
          } catch {
            showSnackbar('Sepet yenilenirken hata oluştu!', 'error');
          }
        } else {
          showSnackbar('Ürün taşınırken hata oluştu!', 'error');
        }
        return;
      }
    }

    // Update right cart + ref'i de güncelle (closure sorunu için)
    if (moveQty >= sourceItem.quantity) {
      setRightCart((prev) => {
        const newCart = prev.filter((item) => item.id !== itemId);
        rightCartRef.current = newCart;
        return newCart;
      });
    } else {
      setRightCart((prev) => {
        const newCart = prev.map((item) =>
          item.id === itemId ? { ...item, quantity: item.quantity - moveQty } : item
        );
        rightCartRef.current = newCart;
        return newCart;
      });
    }

    // Update left cart + ref'i de güncelle
    setLeftCart((prev) => {
      const existing = prev.find((item) => item.id === itemId);
      let newCart: CartItem[];
      if (existing) {
        newCart = prev.map((item) =>
          item.id === itemId ? { ...item, quantity: item.quantity + moveQty } : item
        );
      } else {
        newCart = [...prev, { ...sourceItem, quantity: moveQty }];
      }
      leftCartRef.current = newCart;
      return newCart;
    });
  };

  /**
   * Sepetleri birleştir ve modalı kapat
   */
  const handleMergeAndClose = async () => {
    // ✅ RESTORAN MODU: SQL'de TÜM sepetleri birleştir (mergeAll kullan)
    if (isRestaurantMode && tableId) {
      try {
        setIsLoading(true);
        console.log('🔄 [SPLIT CART - SQL] TÜM sepetler birleştiriliyor...', { tableId, splitSaleId, companyId });
        
        // ✅ FIX: mergeAll kullan - tüm split satırlarını temizler
        // splitSaleId olsa da olmasa da mergeAll çağır (boş split satırları da temizlensin)
        const mergeResponse = await restaurantSplitApi.mergeAll(tableId);
        
        if (!mergeResponse.Success) {
          // Eğer hiç split yoksa bu hata vermez, sadece bilgi mesajı döner
          console.log('ℹ️ [SPLIT CART - SQL] Birleştirme yanıtı:', mergeResponse);
        }
        
        console.log('✅ [SPLIT CART - SQL] Tüm sepetler birleştirildi');
        
        // State'leri temizle
        setSplitSaleId(null);
        setSqlRightItems([]);
        
        // Verileri SQL'den yeniden yükle
        await loadSplitCartsFromSQL();
        
        showSnackbar('Sepetler birleştirildi!', 'success');
        onClose();
        return;
      } catch (error: any) {
        console.error('❌ [SPLIT CART - SQL] Birleştirme hatası:', error);
        // ✅ FIX: Hata alsak bile state'i tazeleyelim ki modal stale kalmasın
        try {
          setSplitSaleId(null);
          await loadSplitCartsFromSQL();
        } catch { /* sessiz */ }
        const status = error?.response?.status;
        const msg = status === 500
          ? 'Sepetler birleştirilirken sunucu hatası. Sepet yenilendi, lütfen tekrar deneyin.'
          : 'Sepetler birleştirilirken hata oluştu!';
        showSnackbar(msg, 'error');
        return;
      } finally {
        setIsLoading(false);
      }
    }
    
    // NORMAL HIZLI SATIŞ: Local state ile birleştir
    const mergedCart = [...leftCart];
    
    rightCart.forEach((rightItem) => {
      const existing = mergedCart.find((item) => item.id === rightItem.id);
      if (existing) {
        existing.quantity += rightItem.quantity;
      } else {
        mergedCart.push({ ...rightItem });
      }
    });

    // Ana sepeti güncelle
    setCart(mergedCart);
    
    // Sağ sepeti temizle
    onRightCartChange?.([]);
    onRightInvoiceNumberChange?.('');
    
    showSnackbar('Sepetler birleştirildi!', 'success');
    onClose();
  };

  /**
   * Ödeme işlemi gerçekleştir
   * @param cartType - Hangi sepet ('left' veya 'right')
   * @param paymentType - Ödeme tipi ('cash', 'pos', 'partial')
   */
  const handlePayment = async (cartType: 'left' | 'right', paymentType: 'cash' | 'pos' | 'partial') => {
    // ✅ FIX: Güncel değerleri ref'lerden al (closure sorunu önlenir)
    const currentLeftCart = leftCartRef.current;
    const currentRightCart = rightCartRef.current;
    const currentRightInvoiceNumber = rightInvoiceNumberRef.current;
    
    // ✅ DEBUG: Tüm değerleri logla
    console.log('🔍 [SPLIT CART PAYMENT] handlePayment başladı:', {
      cartType,
      paymentType,
      'rightInvoiceNumber (state)': rightInvoiceNumber || '(BOŞ)',
      'rightInvoiceNumberRef.current': currentRightInvoiceNumber || '(BOŞ)',
      'mainInvoiceNumber (prop)': mainInvoiceNumber || '(BOŞ)',
      leftCartLength: currentLeftCart.length,
      rightCartLength: currentRightCart.length
    });
    
    const cart = cartType === 'left' ? currentLeftCart : currentRightCart;
    const totals = cartType === 'left' ? leftTotals : rightTotals;
    const invoiceNum = cartType === 'left' ? mainInvoiceNumber : currentRightInvoiceNumber;
    
    if (cart.length === 0) {
      showSnackbar('Sepet boş!', 'warning');
      return;
    }
    
    // ✅ UYARI: Invoice number boş ise
    if (!invoiceNum) {
      console.error('❌ [SPLIT CART PAYMENT] UYARI: Invoice numarası BOŞ!', {
        cartType,
        mainInvoiceNumber: mainInvoiceNumber || '(BOŞ)',
        currentRightInvoiceNumber: currentRightInvoiceNumber || '(BOŞ)'
      });
    }

    console.log(`💰 [SPLIT CART] ${cartType} sepet ${paymentType} ödeme başlatılıyor...`, {
      cart,
      total: totals.grandTotal,
      invoiceNumber: invoiceNum || '(BOŞ)',
      leftCartLength: currentLeftCart.length,
      rightCartLength: currentRightCart.length
    });

    // onProcessPayment callback'i varsa kullan
    if (onProcessPayment) {
      setIsLoading(true);
      try {
        // ✅ YENİ: Ödeme sonrası yeni cart'ı hesapla (ödeme yapılmayan sepet)
        // Sepet 1 ödendiyse → Sepet 2 yeni ana cart olacak
        // Sepet 2 ödendiyse → Sepet 1 yeni ana cart olacak
        const newCartAfterPayment = cartType === 'left' 
          ? [...currentRightCart]  // Sepet 1 ödendi, Sepet 2 yeni ana cart
          : [...currentLeftCart];  // Sepet 2 ödendi, Sepet 1 yeni ana cart
        
        console.log(`📦 [SPLIT CART] newCartAfterPayment hesaplandı: ${newCartAfterPayment.length} ürün (${cartType === 'left' ? 'Sepet 2' : 'Sepet 1'})`);

        // ✅ RESTORAN MODU FIX: Sadece bölünen sepetin satışı (ActiveSale) kapatılmalı.
        // Sol (Sepet 1) → mainSaleId, Sağ (Sepet 2) → splitSaleId.
        // Böylece ödenmemiş taraftaki ürünler ve masa açık kalır.
        const restaurantActiveSaleId: number | null = isRestaurantMode
          ? (cartType === 'left' ? (mainSaleId ?? null) : (splitSaleId ?? null))
          : null;
        if (isRestaurantMode) {
          console.log('🎯 [SPLIT CART - RESTORAN] Kapatılacak ActiveSale Id:', {
            cartType,
            mainSaleId,
            splitSaleId,
            restaurantActiveSaleId,
          });
        }

        const success = await onProcessPayment(
          cartType,
          paymentType,
          cart,
          totals.grandTotal,
          invoiceNum,
          newCartAfterPayment, // ✅ YENİ: Ödeme sonrası yeni cart
          restaurantActiveSaleId // ✅ YENİ: Restoran modunda hangi satışın kapatılacağı
        );

        if (success) {
          // ✅ YENİ: Parçalı ödeme için özel durum - modal sadece açıldı, sepet boşaltılmayacak
          if (paymentType === 'partial') {
            console.log(`📝 [SPLIT CART] ${cartType} sepet için parçalı ödeme modalı açıldı, sepet boşaltılmıyor`);
            // Parçalı ödeme modalı açıldı, loading'i kapat ve çık
            // Sepet, parçalı ödeme tamamlandığında QuickSaleScreen'deki handleClosePartialPaymentModal tarafından boşaltılacak
            return;
          }
          
          // ✅ FIX: Güncel cart değerlerini ref'lerden al (closure sorunu önlenir)
          const currentLeftCartAfter = leftCartRef.current;
          const currentRightCartAfter = rightCartRef.current;
          
          // Ödeme başarılı - ilgili sepeti boşalt
          if (cartType === 'left') {
            console.log('✅ [SPLIT CART] Sol sepet (Sepet 1) ödemesi başarılı, sepet boşaltılıyor...');
            console.log('📊 [SPLIT CART] Güncel sağ sepet:', currentRightCartAfter.length, 'ürün');
            
            // ✅ YENİ: Modal kapanırken setCart yapılmaması için flag
            paymentJustCompletedRef.current = true;
            
            // ✅ FIX: Sağ sepet varsa, sağ sepeti sol sepete taşı (modal içinde görünsün diye)
            if (currentRightCartAfter.length > 0) {
              console.log('🔄 [SPLIT CART] Sepet 2 artık Sepet 1 oldu, modal içinde güncelleniyor...');
              // Sağ sepetteki ürünleri sol sepete taşı
              setLeftCart([...currentRightCartAfter]);
              leftCartRef.current = [...currentRightCartAfter];
              // Sağ sepeti boşalt
              setRightCart([]);
              setRightInvoiceNumber('');
              rightCartRef.current = [];
              rightInvoiceNumberRef.current = '';
              onRightCartChange?.([]);
              onRightInvoiceNumberChange?.('');
            } else {
              // Sağ sepet boşsa, sadece sol sepeti boşalt
              setLeftCart([]);
              leftCartRef.current = [];
            }
            showSnackbar('Sepet 1 ödemesi tamamlandı!', 'success');
          } else {
            console.log('✅ [SPLIT CART] Sağ sepet (Sepet 2) ödemesi başarılı, sepet boşaltılıyor...');
            console.log('📊 [SPLIT CART] Güncel sol sepet:', currentLeftCartAfter.length, 'ürün');
            
            // ✅ YENİ: Sağ sepet ödendiğinde de flag set et (güvenlik için)
            paymentJustCompletedRef.current = true;
            
            setRightCart([]);
            rightCartRef.current = [];
            
            // ✅ YENİ: Cart artık QuickSaleScreen'deki successCallback içinde set ediliyor
            // setTimeout'a gerek yok!
            
            // Sağ sepet boşaldığında dışarıya bildir
            onRightCartChange?.([]);
            
            // B index'i artır (bir sonraki bölmede B3, B4... kullanılacak)
            const newBIndex = splitBIndex + 1;
            onSplitBIndexChange?.(newBIndex);
            
            // ✅ Sol sepette hala ürün varsa, Sepet 2 için hemen yeni fiş numarası oluştur
            if (currentLeftCartAfter.length > 0 && mainInvoiceNumber) {
              const newRightInvoiceNumber = `${mainInvoiceNumber}-B${newBIndex}`;
              console.log(`🔢 [SPLIT CART] Sol sepette ürün var, Sepet 2 için yeni fiş numarası oluşturuluyor: ${newRightInvoiceNumber}`);
              setRightInvoiceNumber(newRightInvoiceNumber);
              rightInvoiceNumberRef.current = newRightInvoiceNumber;
              onRightInvoiceNumberChange?.(newRightInvoiceNumber);
            } else {
              setRightInvoiceNumber('');
              rightInvoiceNumberRef.current = '';
              onRightInvoiceNumberChange?.('');
            }
            
            console.log(`🔄 [SPLIT CART] Sepet 2 ödendi ve temizlendi, bir sonraki bölmede B${newBIndex} kullanılacak`);
            showSnackbar('Sepet 2 ödemesi tamamlandı!', 'success');
          }
          console.log(`✅ [SPLIT CART] ${cartType} sepet ödemesi başarılı, sepet boşaltıldı`);
          
          // ✅ RESTORAN MODU: Tüm ödemeler tamamlandıysa modal kapat ve masalar ekranına dön
          if (isRestaurantMode) {
            // Güncel sepet durumlarını kontrol et
            const leftEmpty = cartType === 'left' 
              ? (currentRightCartAfter.length === 0) // Sol ödendi, sağ da boşsa
              : (leftCartRef.current.length === 0); // Sağ ödendi, sol da boşsa
            const rightEmpty = cartType === 'right' 
              ? true // Sağ zaten ödendi
              : (rightCartRef.current.length === 0); // Sol ödendi, sağı kontrol et
              
            // Her iki sepet de boşsa tüm ödemeler tamamlanmış demektir
            const allPaymentsComplete = cartType === 'left' 
              ? (currentRightCartAfter.length === 0) // Sepet 1 ödendi ve Sepet 2 boştu
              : (currentLeftCartAfter.length === 0); // Sepet 2 ödendi ve Sepet 1 boştu
              
            if (allPaymentsComplete) {
              console.log('🏁 [SPLIT CART - RESTORAN] Tüm ödemeler tamamlandı, modal kapatılıp masalar ekranına dönülüyor...');
              
              if (onRestaurantPaymentComplete) {
                // Callback varsa çağır (masalar ekranına dönüş + satış tamamlama)
                setTimeout(() => {
                  onRestaurantPaymentComplete();
                }, 500);
              } else {
                // Callback yoksa sadece modal kapat
                setTimeout(() => {
                  onClose();
                }, 500);
              }
            }
          }
        } else {
          showSnackbar('Ödeme işlemi başarısız!', 'error');
        }
      } catch (error) {
        console.error(`❌ [SPLIT CART] ${cartType} sepet ödeme hatası:`, error);
        showSnackbar('Ödeme sırasında bir hata oluştu!', 'error');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Fallback: onProcessPayment yoksa uyarı göster
      showSnackbar('Ödeme işlevi henüz bağlanmamış!', 'warning');
    }
  };

  // Sol sepet ödeme butonları
  const handleLeftCashPayment = () => handlePayment('left', 'cash');
  const handleLeftPosPayment = () => handlePayment('left', 'pos');
  const handleLeftPartialPayment = () => handlePayment('left', 'partial');

  // Sağ sepet ödeme butonları
  const handleRightCashPayment = () => handlePayment('right', 'cash');
  const handleRightPosPayment = () => handlePayment('right', 'pos');
  const handleRightPartialPayment = () => handlePayment('right', 'partial');

  return (
    <Dialog
      open={open}
      onClose={handleCloseAndSave}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '85vh',
          maxHeight: isMobile ? '100%' : '85vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CallSplitIcon />
          <Typography variant="h6">SEPET BÖLME</Typography>
          {tableName && (
            <Chip
              label={tableName}
              size="small"
              sx={{ bgcolor: 'primary.dark', color: 'white' }}
            />
          )}
        </Box>
        <IconButton
          onClick={handleCloseAndSave}
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          p: 2,
          overflow: 'hidden',
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Left Cart */}
            <CartPanel
              title="SEPET 1"
              invoiceNumber={mainInvoiceNumber}
              items={leftCart}
              total={leftTotals.grandTotal}
              onMoveItem={handleMoveToRight}
              moveDirection="right"
              onCashPayment={handleLeftCashPayment}
              onPosPayment={handleLeftPosPayment}
              onPartialPayment={handleLeftPartialPayment}
              color="primary"
              isRestaurantMode={isRestaurantMode} // ✅ Restoran modunda KDV dahil fiyat
            />

            {/* Right Cart */}
            <CartPanel
              title="SEPET 2"
              invoiceNumber={rightInvoiceNumber}
              items={rightCart}
              total={rightTotals.grandTotal}
              onMoveItem={handleMoveToLeft}
              moveDirection="left"
              onCashPayment={handleRightCashPayment}
              onPosPayment={handleRightPosPayment}
              onPartialPayment={handleRightPartialPayment}
              color="secondary"
              isRestaurantMode={isRestaurantMode} // ✅ Restoran modunda KDV dahil fiyat
            />
          </>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          color="error"
          startIcon={<MergeIcon />}
          onClick={handleMergeAndClose}
        >
          Sepetleri Birleştir ve Kapat
        </Button>
        <Button
          variant="contained"
          onClick={handleCloseAndSave}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitCartModal;
